/**
 * Photo-led gallery layouts (posters, editorial spreads, stories, thumbnails). The photo is a
 * `stock:<query>` placeholder that becomes a real Unsplash photo when the template is used
 * (and in gallery previews). Positions are proportional, so one design serves several sizes.
 */
import { createImageLayer, createShapeLayer, newLayerId } from '../factory';
import type { Fill, ImageLayer, TemplateDoc } from '../template';
import { Builder, type BuildInput } from './layouts';

/** Image src placeholder: `stock:<orientation>:<search query>`. */
export const STOCK_SRC_PREFIX = 'stock:';
export type StockOrientation = 'landscape' | 'portrait' | 'squarish';

export function stockSrc(query: string, orientation?: StockOrientation) {
  return `${STOCK_SRC_PREFIX}${orientation ?? ''}:${query}`;
}

export function parseStockSrc(
  src: string,
): { query: string; orientation?: StockOrientation } | null {
  if (!src.startsWith(STOCK_SRC_PREFIX)) return null;
  const rest = src.slice(STOCK_SRC_PREFIX.length);
  const i = rest.indexOf(':');
  const o = rest.slice(0, i);
  return {
    query: rest.slice(i + 1),
    orientation: (['landscape', 'portrait', 'squarish'] as const).find((x) => x === o),
  };
}

const orient = (w: number, h: number): StockOrientation =>
  w > h * 1.15 ? 'landscape' : h > w * 1.15 ? 'portrait' : 'squarish';

function photo(
  b: Builder,
  name: string,
  query: string,
  box: { x: number; y: number; width: number; height: number },
  extra: Partial<ImageLayer> = {},
) {
  return b.add(
    createImageLayer({
      id: newLayerId(),
      name,
      ...box,
      fit: 'cover',
      src: stockSrc(query, orient(box.width, box.height)),
      ...extra,
    }),
  );
}

function shade(
  b: Builder,
  fill: Fill,
  box?: { x: number; y: number; width: number; height: number },
) {
  const { width: w, height: h } = b.input;
  return b.add(
    createShapeLayer({
      id: newLayerId(),
      name: 'shade',
      x: 0,
      y: 0,
      width: w,
      height: h,
      ...box,
      fill,
    }),
  );
}

const fade = (to: string, from = '00', angle = 180): Fill => ({
  type: 'linear',
  angle,
  stops: [
    { offset: 0, color: `${to}${from}` },
    { offset: 1, color: `${to}E6` },
  ],
});

/** Full-bleed photo with a huge condensed headline; `bar` puts the words on a black band. */
function posterBase(input: BuildInput, bar: boolean): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p, width: w, height: h } = input;
  const m = b.px(64);
  photo(b, 'photo', input.photo ?? 'landscape', { x: 0, y: 0, width: w, height: h });
  shade(b, bar ? fade('#000000', '00') : fade('#000000', '22', 160));
  if (copy.kicker)
    b.text('kicker', null, {
      text: copy.kicker,
      x: m,
      y: m,
      width: w - m * 2,
      height: b.px(56),
      fontFamily: p.bodyFont,
      fontWeight: 700,
      fontSize: b.px(26),
      letterSpacing: b.px(3),
      textTransform: 'uppercase',
      color: p.accentText,
      maxLines: 1,
      autoFit: true,
      minFontSize: b.px(10),
      highlight: { color: p.accent, paddingX: b.px(14), paddingY: b.px(6), radius: 0 },
    });
  const titleH = Math.round(h * (bar ? 0.26 : 0.42));
  b.text('title', null, {
    text: copy.title,
    x: bar ? Math.round(w * 0.08) : m,
    y: bar ? Math.round(h / 2 - titleH / 2) : Math.round(h * 0.5 - titleH * 0.35),
    width: bar ? Math.round(w * 0.84) : w - m * 2,
    height: titleH,
    fontFamily: p.titleFont,
    fontWeight: bar ? 400 : 700,
    fontSize: Math.round(titleH * (bar ? 0.62 : 0.5)),
    lineHeight: bar ? 1.2 : 0.95,
    letterSpacing: bar ? b.px(18) : 0,
    textTransform: 'uppercase',
    align: 'center',
    verticalAlign: 'middle',
    color: '#FFFFFF',
    autoFit: true,
    maxLines: bar ? 1 : 3,
    minFontSize: b.px(20),
    shadow: bar ? null : { x: 0, y: b.px(6), blur: b.px(30), color: '#000000AA' },
    highlight: bar ? { color: '#0A0A0A', paddingX: b.px(40), paddingY: 0, radius: 0 } : null,
  });
  if (copy.subtitle)
    b.text('subtitle', null, {
      text: copy.subtitle,
      x: m,
      y: h - m - b.px(bar ? 70 : 110),
      width: w - m * 2,
      height: b.px(bar ? 70 : 110),
      fontSize: b.px(30),
      fontWeight: 500,
      lineHeight: 1.3,
      align: bar ? 'center' : 'left',
      verticalAlign: 'bottom',
      color: '#FFFFFF',
      maxLines: 2,
      autoFit: true,
      minFontSize: b.px(12),
    });
  return b.doc;
}

export function posterLayout(input: BuildInput) {
  return posterBase(input, false);
}
export function barLayout(input: BuildInput) {
  return posterBase(input, true);
}

/** Photo + paper panel with a serif headline (magazine spread). */
export function editorialLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p, width: w, height: h } = input;
  const wide = w > h * 1.15;
  const m = b.px(64);
  const panel = wide
    ? { x: Math.round(w * 0.58), y: 0, width: Math.round(w * 0.42), height: h }
    : { x: 0, y: Math.round(h * 0.6), width: w, height: Math.round(h * 0.4) };
  const pic = wide
    ? { x: 0, y: 0, width: panel.x, height: h }
    : { x: 0, y: 0, width: w, height: panel.y };
  photo(b, 'photo', input.photo ?? 'still life', pic);
  shade(b, p.background, panel);
  const inner = { x: panel.x + m, width: panel.width - m * 2 };
  if (copy.kicker)
    b.text('kicker', null, {
      text: copy.kicker,
      ...inner,
      y: panel.y + m,
      height: b.px(40),
      fontSize: b.px(22),
      fontWeight: 600,
      letterSpacing: b.px(4),
      textTransform: 'uppercase',
      color: p.text,
      maxLines: 1,
      autoFit: true,
      minFontSize: b.px(10),
    });
  const titleY = panel.y + m + b.px(copy.kicker ? 60 : 0);
  const titleH = Math.round(panel.height * (wide ? 0.42 : 0.3));
  b.text('title', null, {
    text: copy.title,
    ...inner,
    y: titleY,
    height: titleH,
    fontFamily: p.titleFont,
    fontWeight: 700,
    fontSize: b.px(wide ? 96 : 92),
    lineHeight: 0.98,
    color: p.title,
    verticalAlign: 'top',
    autoFit: true,
    maxLines: 3,
    minFontSize: b.px(28),
  });
  // The subtitle fits between the headline and the footer line
  const footY = panel.y + panel.height - m - b.px(40);
  const subY = titleY + titleH + b.px(16);
  const subH = Math.min(b.px(90), footY - subY - b.px(16));
  if (copy.subtitle && subH > b.px(24))
    b.text('subtitle', null, {
      text: copy.subtitle,
      ...inner,
      y: subY,
      height: subH,
      fontSize: b.px(28),
      lineHeight: 1.35,
      color: p.text,
      maxLines: 3,
      autoFit: true,
      minFontSize: b.px(12),
    });
  const foot = [copy.brand, copy.price ?? copy.cta].filter(Boolean).join('   ·   ');
  if (foot)
    b.text('footer', null, {
      text: foot,
      ...inner,
      y: footY,
      height: b.px(40),
      fontSize: b.px(22),
      fontWeight: 600,
      letterSpacing: b.px(2),
      textTransform: 'uppercase',
      color: p.title,
      maxLines: 1,
      autoFit: true,
      minFontSize: b.px(10),
    });
  return b.doc;
}

/** Story: photo, soft dark gradient, script headline and a glassy card with a list. */
export function storyCardLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p, width: w, height: h } = input;
  const m = b.px(72);
  photo(b, 'photo', input.photo ?? 'portrait', { x: 0, y: 0, width: w, height: h });
  shade(b, {
    type: 'linear',
    angle: 180,
    stops: [
      { offset: 0, color: '#00000088' },
      { offset: 0.4, color: '#00000022' },
      { offset: 1, color: '#000000B0' },
    ],
  });
  b.text('title', null, {
    text: copy.title,
    x: m,
    y: Math.round(h * 0.07),
    width: w - m * 2,
    height: Math.round(h * 0.2),
    fontFamily: p.titleFont,
    fontStyle: 'italic',
    fontWeight: 500,
    fontSize: b.px(110),
    lineHeight: 1,
    align: 'center',
    verticalAlign: 'middle',
    color: '#FFFFFF',
    autoFit: true,
    maxLines: 2,
    minFontSize: b.px(36),
  });
  if (copy.kicker)
    b.text('kicker', null, {
      text: copy.kicker,
      x: m,
      y: Math.round(h * 0.07) - b.px(50),
      width: w - m * 2,
      height: b.px(44),
      fontSize: b.px(24),
      letterSpacing: b.px(6),
      textTransform: 'uppercase',
      align: 'center',
      color: '#FFFFFFCC',
      maxLines: 1,
      autoFit: true,
      minFontSize: b.px(10),
    });
  const rows = (copy.details ?? []).slice(0, 4);
  const rowH = b.px(118);
  const cardH = rows.length * rowH + b.px(40);
  const cardY = Math.round(h * 0.55);
  if (rows.length) {
    b.add(
      createShapeLayer({
        id: newLayerId(),
        name: 'card',
        x: m,
        y: cardY,
        width: w - m * 2,
        height: cardH,
        borderRadius: b.px(36),
        fill: { type: 'solid', color: '#FFFFFF26' },
        strokeWidth: b.px(2),
        strokeColor: '#FFFFFF66',
      }),
    );
    rows.forEach((row, i) => {
      const [label, value] = row.split('|');
      b.text(`item_${i + 1}`, null, {
        text: label.trim(),
        x: m + b.px(40),
        y: cardY + b.px(20) + i * rowH,
        width: w - m * 2 - b.px(80) - b.px(220),
        height: rowH,
        fontSize: b.px(36),
        fontWeight: 500,
        verticalAlign: 'middle',
        color: '#FFFFFF',
        maxLines: 2,
        autoFit: true,
        minFontSize: b.px(14),
      });
      if (value)
        b.text(`price_${i + 1}`, null, {
          text: value.trim(),
          x: w - m - b.px(40) - b.px(220),
          y: cardY + b.px(20) + i * rowH,
          width: b.px(220),
          height: rowH,
          fontFamily: p.titleFont,
          fontSize: b.px(40),
          fontWeight: 700,
          align: 'right',
          verticalAlign: 'middle',
          color: p.accent,
          maxLines: 1,
          autoFit: true,
          minFontSize: b.px(14),
        });
    });
  }
  if (copy.brand)
    b.text('brand', null, {
      text: copy.brand,
      x: m,
      y: h - m - b.px(48),
      width: w - m * 2,
      height: b.px(48),
      fontSize: b.px(26),
      align: 'center',
      color: '#FFFFFFCC',
      maxLines: 1,
      autoFit: true,
      minFontSize: b.px(10),
    });
  return b.doc;
}

/** Thumbnail: photo with stacked, tilted highlighted lines ("HOW TO / CREATE / …"). */
export function stackLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p, width: w, height: h } = input;
  const wide = w > h * 1.15;
  const m = b.px(56);
  photo(b, 'photo', input.photo ?? 'person', { x: 0, y: 0, width: w, height: h });
  if (!wide) shade(b, fade('#000000', '00'));
  const lines = (copy.details?.length ? copy.details : copy.title.split(/\s*\n\s*/)).slice(0, 4);
  const colX = wide ? Math.round(w * 0.46) : m;
  const colW = wide ? w - colX - m : w - m * 2;
  const lineH = Math.round(Math.min(h * (wide ? 0.19 : 0.11), colW * 0.24));
  const top = wide ? Math.round(h / 2 - (lines.length * lineH) / 2) : Math.round(h * 0.58);
  lines.forEach((line, i) => {
    const alt = i % 2 === 1;
    b.text(i === 0 ? 'title' : `line_${i + 1}`, null, {
      text: line,
      x: colX,
      y: top + i * lineH,
      width: colW,
      height: lineH,
      rotation: alt ? 2 : -3,
      fontFamily: p.titleFont,
      fontWeight: 900,
      fontSize: Math.round(lineH * 0.72),
      lineHeight: 1.1,
      textTransform: 'uppercase',
      align: wide ? 'right' : 'left',
      verticalAlign: 'middle',
      color: alt ? p.accentText : p.title,
      maxLines: 1,
      autoFit: true,
      minFontSize: b.px(14),
      highlight: {
        color: alt ? p.accent : '#FFFFFFEE',
        paddingX: Math.round(lineH * 0.18),
        paddingY: Math.round(lineH * 0.02),
        radius: b.px(6),
      },
    });
  });
  if (copy.kicker)
    b.text('kicker', null, {
      text: copy.kicker,
      x: m,
      y: m,
      width: Math.round(w * 0.5),
      height: b.px(50),
      fontWeight: 800,
      fontSize: b.px(26),
      textTransform: 'uppercase',
      letterSpacing: b.px(2),
      color: '#FFFFFF',
      maxLines: 1,
      autoFit: true,
      minFontSize: b.px(10),
      highlight: { color: '#E11D48', paddingX: b.px(14), paddingY: b.px(6), radius: b.px(6) },
    });
  return b.doc;
}

/** Two photos side by side with "before / after" labels and a caption band. */
export function beforeAfterLayout(input: BuildInput): TemplateDoc {
  const b = new Builder(input);
  const { copy, palette: p, width: w, height: h } = input;
  const half = Math.round(w / 2);
  photo(b, 'before', input.photo ?? 'old room', { x: 0, y: 0, width: half, height: h });
  photo(b, 'after', input.photo2 ?? 'modern room', { x: half, y: 0, width: w - half, height: h });
  b.add(
    createShapeLayer({
      id: newLayerId(),
      name: 'divider',
      x: half - b.px(5),
      y: 0,
      width: b.px(10),
      height: h,
      fill: { type: 'solid', color: '#FFFFFF' },
    }),
  );
  const [beforeLabel, afterLabel] = copy.details ?? ['Before', 'After'];
  const label = (name: string, text: string, x: number, rot: number) =>
    b.text(name, null, {
      text,
      x,
      y: b.px(50),
      width: half - b.px(100),
      height: b.px(110),
      rotation: rot,
      fontFamily: p.titleFont,
      fontWeight: 900,
      fontSize: b.px(76),
      textTransform: 'uppercase',
      align: 'center',
      verticalAlign: 'middle',
      color: '#FFFFFF',
      maxLines: 1,
      autoFit: true,
      minFontSize: b.px(18),
      highlight: { color: '#0A0A0A', paddingX: b.px(24), paddingY: b.px(2), radius: b.px(4) },
    });
  label('label_before', beforeLabel ?? 'Before', b.px(50), -3);
  label('label_after', afterLabel ?? 'After', half + b.px(50), 3);
  const bandH = Math.round(h * 0.16);
  b.text('title', null, {
    text: copy.title,
    x: Math.round(w * 0.12),
    y: h - bandH - b.px(36),
    width: Math.round(w * 0.76),
    height: bandH,
    fontFamily: p.titleFont,
    fontWeight: 700,
    fontSize: Math.round(bandH * 0.5),
    letterSpacing: b.px(8),
    textTransform: 'uppercase',
    align: 'center',
    verticalAlign: 'middle',
    color: '#FFFFFF',
    maxLines: 1,
    autoFit: true,
    minFontSize: b.px(14),
    background: { type: 'solid', color: '#000000A6' },
    borderRadius: b.px(16),
  });
  return b.doc;
}
